import type { DateTime } from 'luxon'
import { uploadFile } from '../utils/aws'
import { adminCreateUserDocument } from './admin/userDocumentsActions'
import { createSingleUserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { Dispatch } from '../utils/typeHelpers'

export interface UploadUserDocumentPayload {
  file: File
  user: { uuid: string; id: number }
  transactionId?: number
  documentType: string
  statementMonth?: DateTime
  statementFinancialAccountId?: number
  userDocumentCategoryId?: number | null
  customerDescription?: string
  year?: string
  statement?: {
    statementMonth: string
    financialAccountIds: number[]
  }
  providerId?: number | null
  onProgress?: (percent: number) => void
}

export interface UploadError {
  error: string
}

const generateAwsFileName = (userUuid: string, fileName: string) =>
  `${userUuid}_${crypto.randomUUID()}_${fileName}`

// Admin Action
export const uploadDocument =
  ({
    file,
    user,
    transactionId,
    documentType,
    statementMonth,
    statementFinancialAccountId,
    userDocumentCategoryId,
    statement,
    year,
    providerId,
  }: UploadUserDocumentPayload) =>
  async (dispatch: Dispatch) => {
    try {
      const fileName = generateAwsFileName(user.uuid, file.name)
      const uploadError = await uploadFile(file, fileName)
      if (uploadError?.error) return uploadError
      return dispatch(
        adminCreateUserDocument({
          awsFileName: fileName,
          fileDescription: file.name,
          userId: user.id,
          transactionId,
          documentType,
          statementMonth: statementMonth || null,
          statementFinancialAccountId: statementFinancialAccountId || null,
          userDocumentCategoryId: userDocumentCategoryId || null,
          statement,
          year: year ? year.toString() : undefined,
          providerId,
        })
      )
    } catch (_) {
      return { error: 'Error uploading file.' }
    }
  }

// User action
export const userUploadDocument =
  ({
    file,
    user,
    transactionId,
    documentType,
    statementMonth,
    statementFinancialAccountId,
    userDocumentCategoryId,
    customerDescription,
    year,
    statement,
    providerId,
    onProgress,
  }: UploadUserDocumentPayload) =>
  async (dispatch: Dispatch) => {
    try {
      const fileName = generateAwsFileName(user.uuid, file.name)
      const uploadError = await uploadFile(file, fileName, onProgress)
      if (uploadError?.error) return uploadError
      return dispatch(
        createSingleUserDocument({
          awsFileName: fileName,
          fileDescription: file.name,
          transactionId: transactionId || null,
          documentType,
          statementMonth: statementMonth || null,
          statementFinancialAccountId: statementFinancialAccountId || null,
          userDocumentCategoryId: userDocumentCategoryId || null,
          customerDescription: customerDescription || null,
          bookkeepingReportId: null,
          year,
          statement,
          providerId,
        })
      )
    } catch (_) {
      return { error: 'Error uploading file.' }
    }
  }
